import React, { useEffect, useRef, useState } from 'react'

import styles from './DismissibleAlert.module.css'

// Notifications hang around until the page is refreshed unless something takes
// them down, so every banner auto-hides after this long and can also be closed
// by hand.
export const ALERT_AUTO_DISMISS_MS = 5000

/**
 * Bootstrap alert that hides itself after `timeout` ms and offers a close (x)
 * button.
 *
 * Pass `resetKey` when the message is driven by state the alert cannot clear
 * (Redux errors, for example): whenever the key changes the alert becomes
 * visible again and the timer restarts, so repeating an action re-shows the
 * feedback instead of staying hidden.
 */
export default function DismissibleAlert({
  type = 'danger',
  className = '',
  style,
  role,
  onRef,
  onDismiss,
  autoDismiss = true,
  timeout = ALERT_AUTO_DISMISS_MS,
  resetKey,
  children
}) {
  const [visible, setVisible] = useState(true)
  const onDismissRef = useRef(onDismiss)
  onDismissRef.current = onDismiss

  useEffect(() => {
    setVisible(true)
  }, [resetKey])

  useEffect(() => {
    if (!visible || !autoDismiss) {
      return undefined
    }
    const timerId = setTimeout(() => {
      setVisible(false)
      if (onDismissRef.current) {
        onDismissRef.current()
      }
    }, timeout)
    return () => clearTimeout(timerId)
  }, [visible, autoDismiss, timeout, resetKey])

  if (!visible) {
    return null
  }

  const handleClose = event => {
    event.preventDefault()
    setVisible(false)
    if (onDismissRef.current) {
      onDismissRef.current()
    }
  }

  return (
    <div
      className={`alert alert-${type} ${styles.alert} ${className}`}
      style={style}
      role={role}
      ref={onRef}
    >
      <button
        type="button"
        className={styles.closeButton}
        aria-label="Close"
        onClick={handleClose}
      >
        &times;
      </button>
      {children}
    </div>
  )
}
